import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, fmtError } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { GuardianPanel } from "@/components/GuardianPanel";
import { EmergencyMap } from "@/components/EmergencyMap";
import { Clock, MapPin, Users, Radio, ShieldCheck, Star, CheckCircle2, Circle, Home, Bell, Stethoscope, ChevronRight, X, Heart, Flag, AlertTriangle, Navigation2, Wifi, WifiOff, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { TrustBadge } from "@/components/TrustBadge";
import { ReportModal } from "@/components/ReportModal";

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtCountdown = (s) => { if (s <= 0) return "0:00"; const m = Math.floor(s / 60); return `${m}:${String(s % 60).padStart(2, "0")}`; };
const SEV = { Low: "#6b7280", Medium: "#F59E0B", High: "#F97316", Critical: "#DC2626" };
const STATUS_STEPS = [
  { k: "waiting", t: "Waiting" }, { k: "responder_assigned", t: "Assigned" },
  { k: "responder_en_route", t: "En Route" }, { k: "responder_arrived", t: "Arrived" }, { k: "resolved", t: "Resolved" },
];
const ROLE_LABEL = { nearest: "Nearest responder", second: "Second responder", backup: "Backup responder" };
const RSTATUS = {
  notified: { t: "Notified", c: "#6b7280" }, standby: { t: "On standby", c: "#F59E0B" },
  accepted: { t: "Accepted", c: "#16A34A" }, en_route: { t: "En route", c: "#F97316" },
  arrived: { t: "Arrived", c: "#16A34A" }, declined: { t: "Declined", c: "#DC2626" },
};

export default function EmergencyLive() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emg, setEmg] = useState(null);
  const [showSafe, setShowSafe] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const fetchedAtRef = useRef(Date.now());
  const lastOkRef = useRef(Date.now());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/emergencies/${id}`);
      setEmg(data);
      fetchedAtRef.current = Date.now();
      lastOkRef.current = Date.now();
    } catch (e) {
      if (e.response) { toast.error(fmtError(e.response?.data?.detail)); navigate("/home"); }
      // transient/network errors: keep last state, tracker shows "signal delayed"
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (emg?.status === "resolved") return;
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [load, emg?.status]);

  // Lightweight 1s ticker — only runs while a responder is actively moving.
  useEffect(() => {
    if (emg?.status === "resolved") return;
    const moving = (emg?.responders || []).some((r) => r.moving);
    if (!moving) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [emg]);

  const markSafe = async (safe) => {
    setBusy(true);
    try { const { data } = await api.post(`/emergencies/${id}/safe`, { safe, feedback }); setEmg(data); setShowSafe(false); toast.success(safe ? "Marked safe. Stay well." : "Guardian AI has logged that you still need help."); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const respond = async (action) => {
    setBusy(true);
    try { const { data } = await api.post(`/emergencies/${id}/respond`, { action }); setEmg(data); toast.success(action === "accept" ? "You accepted — the resident has been notified." : action === "arrived" ? "Marked as arrived on scene." : "Declined."); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!emg) return <Loader label="Loading emergency…" />;
  const stepIdx = STATUS_STEPS.findIndex((s) => s.k === emg.status);
  const resolved = emg.status === "resolved";

  // ---- Live responder tracking (computed client-side, re-synced each poll) ----
  const secSinceFetch = Math.max(0, (now - fetchedAtRef.current) / 1000);
  const stale = !resolved && (Date.now() - lastOkRef.current) / 1000 > 8;
  const liveResponders = emg.responders.map((r) => {
    if (r.progress == null) return { ...r, remainingFrac: r.status === "arrived" ? 0 : 1 };
    let live = r.progress;
    if (r.moving) {
      const etaSec = Math.max(1, (r.eta_remaining_min || 0) * 60);
      live = Math.min(0.98, r.progress + Math.min(1, secSinceFetch / etaSec) * (1 - r.progress));
    } else if (r.status === "arrived") {
      live = 1;
    }
    const etaSecLive = r.status === "arrived" ? 0 : Math.max(0, Math.round((r.eta_remaining_min || 0) * 60 - secSinceFetch));
    return { ...r, liveProgress: live, remainingFrac: 1 - live, liveRemainingKm: +(r.distance_km * (1 - live)).toFixed(2), liveEtaSec: etaSecLive };
  });
  const arrivedLead = liveResponders.find((r) => r.status === "arrived");
  const movingLead = liveResponders.filter((r) => r.moving).sort((a, b) => a.liveEtaSec - b.liveEtaSec)[0];
  const lead = arrivedLead || movingLead;

  return (
    <div className="min-h-screen bg-[#FFF7E6] pb-32" data-testid="emergency-live">
      <div className="max-w-md mx-auto px-5 pt-6">
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-[#DC2626]/10 text-[#DC2626] px-3 py-1 rounded-full"><span className="w-2 h-2 rounded-full bg-[#DC2626] animate-pulse" /> Live</span>
          <div className="flex items-center gap-2">
            {!emg.viewer?.is_victim && <button onClick={() => setShowReport(true)} data-testid="emg-report" className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm text-[#DC2626]"><Flag className="w-4 h-4" /></button>}
            <button onClick={() => navigate("/home")} data-testid="emg-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <GuardianPanel status={emg.guardian_status} />

        {emg.is_demo && (
          <div className="mt-4 bg-[#F59E0B]/15 border border-[#F59E0B]/40 rounded-2xl px-4 py-3 flex items-start gap-2.5" data-testid="test-mode-banner">
            <FlaskConical className="w-4 h-4 text-[#92400e] mt-0.5 shrink-0" />
            <p className="text-[13px] leading-snug text-[#92400e] font-semibold"><span className="uppercase tracking-wide">Test mode</span> — this is a practice run. No responders or emergency services are contacted.</p>
          </div>
        )}

        {emg.fake_suspected && (
          <div className="mt-4 bg-[#F59E0B]/15 border border-[#F59E0B]/40 rounded-2xl p-4 flex items-start gap-3" data-testid="fake-warning">
            <AlertTriangle className="w-5 h-5 text-[#92400e] mt-0.5 shrink-0" />
            <p className="text-sm text-[#92400e] font-medium">Guardian AI flagged this as a possible false alert. Filing false emergencies can lead to account suspension.</p>
          </div>
        )}

        {/* Summary */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-5 mt-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5" data-testid="emg-summary">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black font-head uppercase tracking-tight">{emg.type_label}</h2>
            <span className="text-xs font-bold text-white px-3 py-1 rounded-full" style={{ background: SEV[emg.severity] }}>{emg.severity}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <Info icon={MapPin} label="Location" value={emg.location.label} />
            <Info icon={Home} label="Community" value={emg.community_name} />
            <Info icon={Clock} label="Started" value={fmtTime(emg.started_at)} />
            <Info icon={Users} label="Trusted contacts" value={emg.summary.trusted_contacts} />
            <Info icon={Radio} label="Nearby responders" value={emg.summary.nearby_responders} />
            <Info icon={Clock} label="ETA" value={emg.eta_min != null ? `${emg.eta_min} min` : "—"} />
          </div>
          {emg.needs?.length > 0 && <div className="flex flex-wrap gap-2 mt-4">{emg.needs.map((n) => <span key={n} className="text-xs font-semibold bg-[#C4B5FD]/25 text-[#4c1d95] px-2.5 py-1 rounded-full">{n}</span>)}</div>}
        </motion.div>

        {/* Status stepper */}
        <div className="bg-white rounded-3xl p-5 mt-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5">
          <div className="flex items-center justify-between">
            {STATUS_STEPS.map((s, i) => (
              <div key={s.k} className="flex flex-col items-center flex-1 relative">
                {i > 0 && <div className={`absolute top-2.5 right-1/2 w-full h-0.5 ${i <= stepIdx ? "bg-[#F97316]" : "bg-[#111827]/10"}`} />}
                <div className={`w-5 h-5 rounded-full z-10 flex items-center justify-center ${i <= stepIdx ? "bg-[#F97316]" : "bg-[#111827]/10"}`}>{i <= stepIdx && <div className="w-2 h-2 rounded-full bg-white" />}</div>
                <span className={`text-[10px] font-semibold mt-1.5 text-center ${i <= stepIdx ? "text-[#111827]" : "text-[#111827]/40"}`}>{s.t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Live responder tracker */}
        {lead && !resolved && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-[#111827] text-white rounded-3xl p-5 mt-4" data-testid="responder-tracker">
            <div className="flex items-center justify-between mb-3">
              <span className="flex items-center gap-2 font-bold"><Navigation2 className="w-5 h-5 text-[#F97316]" /> {arrivedLead ? `${lead.name} has arrived` : `${lead.name} is on the way`}</span>
              <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${stale ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-[#16A34A]/20 text-[#4ade80]"}`} data-testid="tracker-signal">
                {stale ? <><WifiOff className="w-3 h-3" /> Signal delayed</> : <><Wifi className="w-3 h-3" /> Live</>}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-2xl p-3">
                <p className="text-[11px] uppercase tracking-wide text-white/40 font-bold">Distance away</p>
                <p className="text-2xl font-black font-head" data-testid="tracker-distance">{arrivedLead ? "On scene" : `${lead.liveRemainingKm} km`}</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-3">
                <p className="text-[11px] uppercase tracking-wide text-white/40 font-bold">Arriving in</p>
                <p className="text-2xl font-black font-head tabular-nums" data-testid="tracker-eta">{arrivedLead ? "—" : fmtCountdown(lead.liveEtaSec)}</p>
              </div>
            </div>
            <div className="h-2 rounded-full bg-white/10 mt-3 overflow-hidden">
              <motion.div className="h-full rounded-full bg-[#F97316]" animate={{ width: `${Math.round((lead.liveProgress ?? (arrivedLead ? 1 : 0)) * 100)}%` }} transition={{ duration: 1, ease: "linear" }} />
            </div>
            {stale && <p className="text-[11px] text-[#F59E0B] mt-2">Waiting for the next live update — showing the last known position.</p>}
          </motion.div>
        )}

        {/* Map */}
        <div className="mt-4"><EmergencyMap victim={emg.location} responders={liveResponders} accuracy={emg.location.accuracy} label={emg.location.label} /></div>

        {/* Responders */}
        <h3 className="text-lg font-bold font-head mt-6 mb-3 px-1">Ranked responders</h3>
        <div className="space-y-3">
          {liveResponders.map((r) => {
            const rs = RSTATUS[r.status] || RSTATUS.notified;
            const showLive = r.moving && r.liveRemainingKm != null;
            return (
              <div key={r.id} className="bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5" data-testid={`responder-${r.role}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-[#C4B5FD]/30 flex items-center justify-center font-bold">{r.name[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold flex items-center gap-1.5 truncate">{r.name}{r.verification && <ShieldCheck className="w-4 h-4 text-[#16A34A] shrink-0" />}{!r.is_demo && <span className="text-[10px] bg-[#16A34A]/15 text-[#16A34A] px-1.5 py-0.5 rounded font-bold">LIVE</span>}</p>
                    <div className="flex items-center gap-1.5 mt-0.5"><TrustBadge badge={r.badge} size="sm" /></div>
                    <p className="text-xs text-[#111827]/50 mt-0.5" data-testid={`responder-eta-${r.role}`}>
                      {ROLE_LABEL[r.role]} · {showLive ? <span className="text-[#F97316] font-semibold">{r.liveRemainingKm} km · {fmtCountdown(r.liveEtaSec)} away</span> : `${r.distance_km} km · ${r.eta_min} min`}
                    </p>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: `${rs.c}22`, color: rs.c }}>{rs.t}</span>
                </div>
                <div className="flex items-center gap-3 mt-2.5 text-[11px] text-[#111827]/50 font-semibold">
                  <span className="flex items-center gap-1"><Star className="w-3 h-3 text-[#F97316]" /> Trust {r.trust_score}</span>
                  <span>{r.prev_responses} prev responses</span>
                  <span className="ml-auto text-[#111827]/40">Rank {r.rank_score}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Timeline */}
        <h3 className="text-lg font-bold font-head mt-6 mb-3 px-1">Live timeline</h3>
        <div className="relative pl-4" data-testid="emg-timeline">
          <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-[#C4B5FD]/50" />
          <AnimatePresence initial={false}>
            {emg.timeline.map((t, i) => (
              <motion.div key={`${t.iso}-${i}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} className="flex gap-4 items-start relative mb-3">
                <div className="w-3.5 h-3.5 rounded-full bg-[#F97316] border-2 border-white z-10 mt-1 shrink-0" />
                <div><p className="text-sm font-semibold">{t.event}</p><p className="text-xs text-[#111827]/40">{fmtTime(t.iso)} · {t.actor}</p></div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Family notifications */}
        <h3 className="text-lg font-bold font-head mt-6 mb-1 px-1 flex items-center gap-2">Family notifications <span className="text-[10px] bg-[#F59E0B]/15 text-[#92400e] px-2 py-0.5 rounded-full">IN-APP</span></h3>
        <div className="space-y-2 mt-2">
          {emg.notifications.length === 0 && <p className="text-sm text-[#111827]/50 px-1">No trusted contacts on file yet.</p>}
          {emg.notifications.map((n, i) => (
            <div key={i} className="bg-white rounded-2xl p-3 flex items-center gap-3 border border-[#111827]/5">
              <div className="w-9 h-9 rounded-full bg-[#C4B5FD]/30 flex items-center justify-center"><Bell className="w-4 h-4 text-[#111827]/60" /></div>
              <div className="flex-1"><p className="font-semibold text-sm">{n.name} <span className="text-[#111827]/40 font-normal">· {n.relationship}</span></p></div>
              <span className="text-xs font-bold text-[#16A34A]">{n.status === "acknowledged" ? "Acknowledged" : "Notified"}</span>
            </div>
          ))}
        </div>

        {/* First aid */}
        <div className="bg-[#111827] text-white rounded-3xl p-5 mt-6" data-testid="emg-first-aid">
          <h3 className="text-lg font-bold font-head flex items-center gap-2 mb-3"><Stethoscope className="w-5 h-5 text-[#C4B5FD]" /> Guardian AI first-aid guidance</h3>
          <ul className="space-y-2">
            {emg.first_aid.map((f, i) => <li key={i} className="flex items-start gap-2 text-white/90"><CheckCircle2 className="w-4 h-4 text-[#16A34A] mt-1 shrink-0" /> {f}</li>)}
          </ul>
          <p className="text-[11px] text-white/40 mt-3">General guidance only. Not a substitute for professional medical advice or official emergency services.</p>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur-md border-t border-[#111827]/10 p-4 z-40">
        <div className="max-w-md mx-auto">
          {resolved ? (
            <button onClick={() => navigate("/home")} data-testid="emg-done" className="w-full bg-[#111827] text-white rounded-2xl py-4 font-bold flex items-center justify-center gap-2"><Home className="w-5 h-5" /> Back to Home</button>
          ) : emg.viewer?.is_victim ? (
            <button onClick={() => setShowSafe(true)} data-testid="emg-mark-safe" className="w-full bg-[#16A34A] text-white rounded-2xl py-4 font-bold flex items-center justify-center gap-2"><ShieldCheck className="w-5 h-5" /> Are you safe now?</button>
          ) : emg.viewer?.can_respond ? (
            emg.viewer.my_status === "accepted" ? (
              emg.viewer.my_arrived
                ? <div className="text-center font-bold text-[#16A34A] py-2">You have arrived on scene ✓</div>
                : <button onClick={() => respond("arrived")} disabled={busy} data-testid="emg-arrived" className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold">Mark arrived on scene</button>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => respond("decline")} disabled={busy} data-testid="emg-decline" className="bg-[#DC2626]/10 text-[#DC2626] rounded-2xl py-4 font-bold">Decline</button>
                <button onClick={() => respond("accept")} disabled={busy} data-testid="emg-accept" className="bg-[#16A34A] text-white rounded-2xl py-4 font-bold">Accept & respond</button>
              </div>
            )
          ) : (
            <button onClick={() => navigate("/home")} data-testid="emg-back" className="w-full bg-[#111827] text-white rounded-2xl py-4 font-bold flex items-center justify-center gap-2"><Home className="w-5 h-5" /> Back to Home</button>
          )}
        </div>
      </div>

      {showSafe && (
        <div className="fixed inset-0 bg-[#111827]/60 z-[70] flex items-end sm:items-center justify-center" onClick={() => setShowSafe(false)}>
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 ll-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-bold font-head">Are you safe now?</h2><button onClick={() => setShowSafe(false)} data-testid="safe-close" className="w-9 h-9 rounded-full bg-[#111827]/5 flex items-center justify-center"><X className="w-5 h-5" /></button></div>
            <textarea data-testid="safe-feedback" value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Optional feedback about this response…" rows={3} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 w-full outline-none focus:border-[#F97316] mb-4" />
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="safe-no" onClick={() => markSafe(false)} disabled={busy} className="bg-[#DC2626]/10 text-[#DC2626] rounded-2xl py-4 font-bold">No, still need help</button>
              <button data-testid="safe-yes" onClick={() => markSafe(true)} disabled={busy} className="bg-[#16A34A] text-white rounded-2xl py-4 font-bold flex items-center justify-center gap-2"><Heart className="w-5 h-5" /> Yes, I'm safe</button>
            </div>
          </div>
        </div>
      )}
      <ReportModal open={showReport} onClose={() => setShowReport(false)} targetEmergencyId={id} targetLabel={`${emg.type_label} · ${emg.community_name}`} />
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-[#F97316] mt-0.5 shrink-0" />
    <div className="min-w-0"><p className="text-[11px] uppercase tracking-wider text-[#111827]/45 font-bold">{label}</p><p className="font-semibold truncate">{value}</p></div>
  </div>
);
